import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/login", { replace: true });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e?.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="reset-pwd-page" className="public-light bg-[#050505] min-h-screen text-[#F5F2EB]">
      <Navbar/>
      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-md mx-auto border border-white/10 bg-[#0A0A0A] p-8 md:p-12">
          <div className="overline mb-3">Account recovery</div>
          <h1 className="font-serif text-3xl mb-2">Set a new password</h1>
          {!token ? (
            <div data-testid="reset-missing-token" className="text-sm text-red-300 mt-6">
              This reset link is missing its token. Please request a new one.
              <div className="mt-4"><Link to="/forgot-password" className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Request a reset link</Link></div>
            </div>
          ) : (
            <>
              <p className="text-[#A1A1AA] text-sm mb-6">Choose a password you don't use anywhere else (min 8 chars).</p>
              <form onSubmit={submit} className="space-y-3">
                <input data-testid="reset-password" type="password" autoComplete="new-password" required value={password}
                  onChange={e => setPassword(e.target.value)} placeholder="New password"
                  className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
                <input data-testid="reset-password-confirm" type="password" autoComplete="new-password" required value={confirm}
                  onChange={e => setConfirm(e.target.value)} placeholder="Confirm password"
                  className="w-full bg-black border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none"/>
                {err && <div data-testid="reset-error" className="text-xs text-red-400 border border-red-500/30 bg-red-500/10 p-2.5">{err}</div>}
                <button data-testid="reset-submit" type="submit" disabled={busy}
                  className="w-full bg-[#D4AF37] text-black py-3.5 font-medium uppercase tracking-[0.18em] text-xs hover:opacity-90 disabled:opacity-60 transition">
                  {busy ? "…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
      <Footer/>
    </div>
  );
}
